// DocTemplateUploadDialog — modal for uploading a document-template file.
// Library documents are not uploaded here; they are authored in Documents
// or filled from a template.
//
// The destination `path` is derived the same way the dashboards/templates
// create flow derives it — `joinChildPath(parentFolder, name)` — so the
// slug rules and uniqueness guarantees stay identical across surfaces.

import { useEffect, useMemo, useRef, useState } from 'react'
import { UploadCloud } from 'lucide-react'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { FolderTreePicker, joinChildPath, type PathItem } from '../../components/explorer'

export interface UploadSubmit {
  file: File
  name: string
  path: string
  /** Template + PDF only: ask the server to add fillable fields via an LLM. */
  autoAddFields?: boolean
}

/** True when the picked file looks like a PDF (by MIME or extension). */
function isPdfFile(file: File | null): boolean {
  if (!file) return false
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

export interface UploadDialogProps {
  open: boolean
  /** Folder rows for the parent picker. */
  folders: readonly PathItem[]
  /** Existing items so implicit parent folders show in the picker. */
  items: readonly PathItem[]
  defaultParentPath: string
  onClose: () => void
  /** Throws on failure (surfaced inline); resolves + closes on success. */
  onSubmit: (input: UploadSubmit) => Promise<void>
}

/** Matches proxy-server reports.MaxFileSizeBytes / docs.MaxFileSizeBytes. */
export const MAX_TEMPLATE_FILE_BYTES = 10 * 1024 * 1024

function formatMaxTemplateSize(): string {
  return `${MAX_TEMPLATE_FILE_BYTES / (1024 * 1024)} MB`
}

function baseName(fileName: string): string {
  const idx = fileName.lastIndexOf('.')
  return idx > 0 ? fileName.slice(0, idx) : fileName
}

function readError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const maybe = err as { data?: { message?: unknown }; status?: unknown }
    const status = typeof maybe.status === 'number' ? maybe.status : undefined
    const message = typeof maybe.data?.message === 'string' ? maybe.data.message : undefined
    if (status === 409) return message ?? 'A report or folder already exists at this path.'
    if (status === 413) return message ?? 'File is too large. Maximum size is 10 MB.'
    if (status === 503) return message ?? 'File storage is not configured on the server.'
    if (message) return message
  }
  return err instanceof Error ? err.message : String(err)
}

const DocTemplateUploadDialog = ({
  open,
  folders,
  items,
  defaultParentPath,
  onClose,
  onSubmit
}: UploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [parentPath, setParentPath] = useState(defaultParentPath)
  const [autoAddFields, setAutoAddFields] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setFile(null)
      setName('')
      setParentPath(defaultParentPath)
      setAutoAddFields(false)
      setError(null)
      setSubmitting(false)
    }
  }, [open, defaultParentPath])

  // The AI-fields option only applies to PDF templates (other formats already
  // support `{placeholder}` text merge fields without help).
  const showAiFields = isPdfFile(file)
  const previewPath = useMemo(() => joinChildPath(parentPath, name), [parentPath, name])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.[0] ?? null
    if (picked && picked.size > MAX_TEMPLATE_FILE_BYTES) {
      setFile(null)
      setError(`File is too large. Maximum size is ${formatMaxTemplateSize()}.`)
      event.target.value = ''
      return
    }
    setFile(picked)
    setError(null)
    // Seed the display name from the file when the user hasn't typed one.
    if (picked && name.trim() === '') setName(baseName(picked.name))
  }

  const handleSubmit = async (): Promise<void> => {
    if (!file) {
      setError('Choose a file to upload.')
      return
    }
    if (file.size > MAX_TEMPLATE_FILE_BYTES) {
      setError(`File is too large. Maximum size is ${formatMaxTemplateSize()}.`)
      return
    }
    if (previewPath === '') {
      setError('Name must contain at least one letter, digit, dash, underscore, or dot.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        file,
        name: name.trim(),
        path: previewPath,
        autoAddFields: showAiFields ? autoAddFields : undefined
      })
      onClose()
    } catch (err) {
      setError(readError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = (): void => {
    if (submitting) return
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-120">
        <DialogHeader>
          <DialogTitle>Upload template</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-template-upload-file">File</Label>
            <input
              ref={fileInputRef}
              id="doc-template-upload-file"
              type="file"
              onChange={handleFileChange}
              disabled={submitting}
              className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-md file:border-0 file:bg-brand-soft file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand hover:file:bg-brand-soft/80"
            />
            {file ? (
              <span className="text-11 text-ink-3">
                {file.name} · {file.type || 'unknown type'}
              </span>
            ) : (
              <span className="text-11 text-ink-3">Maximum size is {formatMaxTemplateSize()}.</span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-template-upload-parent">Parent folder</Label>
            <FolderTreePicker
              id="doc-template-upload-parent"
              folders={folders}
              dashboards={items}
              value={parentPath}
              onChange={setParentPath}
              disabled={submitting}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-template-upload-name">Name</Label>
            <Input
              id="doc-template-upload-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Quarterly summary"
              disabled={submitting}
            />
          </div>

          {showAiFields ? (
            <label
              htmlFor="doc-template-upload-ai-fields"
              className="flex items-start gap-2.5 rounded-md border border-line p-2.5"
            >
              <input
                id="doc-template-upload-ai-fields"
                type="checkbox"
                className="mt-0.5 size-4 accent-brand"
                checked={autoAddFields}
                onChange={(e) => setAutoAddFields(e.target.checked)}
                disabled={submitting}
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm text-ink">Add fillable fields with AI</span>
                <span className="text-11 text-ink-3">
                  This PDF has no form fields. Let an AI scan it and add merge fields next to its
                  labels so documents can be generated from it. May take a few seconds.
                </span>
              </span>
            </label>
          ) : null}

          <div className="flex flex-col gap-1">
            <Label className="text-11 uppercase tracking-wider text-ink-3">Path</Label>
            <code className="rounded bg-bg-sunk px-2 py-1 font-mono text-12 text-ink-2">
              /{previewPath || '…'}
            </code>
          </div>

          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={submitting || file === null || name.trim() === ''}
          >
            <UploadCloud aria-hidden />
            {submitting
              ? showAiFields && autoAddFields
                ? 'Adding fields…'
                : 'Uploading…'
              : 'Upload template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DocTemplateUploadDialog
